import React from 'react';
import { useParams } from 'react-router';
import { useNegotiationTestOverrideContext } from '../../components/NegotiationTestOverride/NegotiationTestOverrideContext';
import { NegotiationOption, NegotiationOptionAlternative, OutDebt, useGetOutDebtNegotiationOptionsQuery, useGetOutDebtsQuery } from '../../generated/graphql';
import { useGetProfile } from '../../utils/useGetProfile';

export const useDebtDetails = () => {
  const { debtId, step: rawStep } = useParams<{ debtId: string, step?: string }>();

  const step = (() => {
    switch (rawStep) {
      default:
      case '': return 'default';
      case 'resumen': return 'summary';
      case 'confirmacion': return 'confirmation';
    }
  })();

  // skip first query, we only need the refetch function
  const { refetch: negOptionsRefetch } = useGetOutDebtNegotiationOptionsQuery({
    variables: { debtId, gracePeriod: 0 }, skip: true,
  });
  const { data: outDebtsData, loading: outDebtsLoading } = useGetOutDebtsQuery({ fetchPolicy: 'network-only' });

  const outDebts = outDebtsData?.outDebts;

  const [ gracePeriod, setGracePeriod ] = React.useState(0);
  const handleChangeGracePeriod = (value: number) => setGracePeriod(value);

  const [ selectedOption, setSelectedOption ] = React.useState<NegotiationOption | null>(null);
  const [ selectedAlternative, setSelectedAlternative ] = React
    .useState<NegotiationOptionAlternative | null>(null);

  const { data: profileData, loading: profileLoading } = useGetProfile();

  // save in own state to control what we show
  const [ negOptions, setNegOptions ] = React.useState<NegotiationOption[]>([]);

  const outDebt = outDebts?.find((item) => item.id === debtId);
  const isLoading = outDebtsLoading || profileLoading;

  // auto invalidate options data and refetch when new info is available (payment capacity updated)
  React.useEffect(() => {
    setNegOptions([]);
    negOptionsRefetch({ debtId, gracePeriod })
      .then((res) => {
        if (!res.data) return;
        setNegOptions(res.data.outDebtNegotiationOptions || []);
      });
  }, [
    gracePeriod, debtId, negOptionsRefetch,
  ]);

  // reset basic info on debt change
  React.useEffect(() => {
    setGracePeriod(0);
    setSelectedOption(null);
    setSelectedAlternative(null);
  }, [ debtId ]);

  React.useEffect(() => {
    const unique = negOptions.length === 1 ? negOptions[0] : null;
    if (!unique) return;
    if (!!unique && unique.alternatives.length === 1) {
      setSelectedAlternative(unique.alternatives[0]);
    }
    setSelectedOption(unique);
  }, [ negOptions, setSelectedOption, setSelectedAlternative ]);

  /**
   * Override negotiation test info
   * This should only be active in development and QA, never in production
   */
  const {
    negTestOverrideActive, negStateTest, prePaymentTest,
  } = useNegotiationTestOverrideContext();
  if (negTestOverrideActive) {
    outDebts?.forEach((od) => {
      if (!od.negotiation) return;
      // eslint-disable-next-line no-param-reassign
      od.negotiation.state = negStateTest;
      // eslint-disable-next-line no-param-reassign
      od.negotiation.alternative.prePayment = prePaymentTest;
    });
    negOptions.forEach((op) => {
      op.alternatives.forEach((alt) => {
        // eslint-disable-next-line no-param-reassign
        alt.prePayment = prePaymentTest;
      });
    });
  }

  return {
    confirmedOption: outDebt?.negotiation,
    outDebt: outDebt as OutDebt,
    outDebts,
    isLoading,
    negOptions,
    gracePeriod,
    onChangeGracePeriod: handleChangeGracePeriod,
    selectedOption,
    setSelectedOption,
    selectedAlternative,
    setSelectedAlternative,
    profileData,
    rawStep,
    step,
  };
};
